import { useAuthModalContext, useIsLoggedInWithLoading } from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { Search } from '@dreamstack/icons/src/icon-components/Search'
import { clsxMerge, cn } from '@dreamstack/util/src/cn'
import Skeleton from '@mui/material/Skeleton'
import { motion, useAnimation } from 'framer-motion'
import map from 'lodash/map'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useUserId } from '../../../auth/src/lib/useUserId'
import { useMyAccentroNavEntry } from '../hooks/useMyAccentroNavEntry'
import { usePageYOffset } from '../hooks/usePageYOffset'
import { useResponsiveHeroVariants } from '../hooks/useResponsiveHeroVariants'
import { useResponsiveNavbarVariants } from '../hooks/useResponsiveNavbarVariants'
import { useRouteChange } from '../hooks/useRouteChange'
import { useScrollDirection } from '../hooks/useScrollDirection'
import { useIsMlOrGreater } from '../hooks/useWindowSize'
import type { HeroSize } from './HeroContainer'
import { useGetInvestorsLocaleForLink } from '../hooks/useGetInvestorsLocaleForLink'
import {
  IS_ACCENTRO_INTERNATIONAL,
  IS_ACCENTRO_WEB,
  SHOW_NAV_SEARCH,
} from '../config/config'
import { ContactHeaderForNavBar } from './ContactHeaderForNavBar'
import { EntryWithSubNavbar, StyledNavEntry } from './EntryWithSubNavbar'
import { LocaleSwitcher } from './LocaleSwitcher'
import { MobileNavBar } from './MobileNavBar'
import { NavBarLogo } from './NavBarLogo'
import { useNavBarEntries } from './useNavBarEntries'

export type NavbarProps = {
  forceDarkTextColor?: boolean
  standalone?: boolean
  isProjectAndProperty?: boolean
  showContactHeader?: boolean
}

type NavbarPropsWithHero = NavbarProps & {
  hasHero: boolean
  heroSize: HeroSize | undefined
}

const NavbarRightElements = () => {
  const locale = useGetInvestorsLocaleForLink()
  return (
    <>
      {SHOW_NAV_SEARCH && (
        <div className="p-px-8 z-10">
          <Link passHref href="/search">
            <Search />
          </Link>
        </div>
      )}
      <div>
        {IS_ACCENTRO_WEB ? (
          <Link
            href={`https://investors.accentro.de/${locale}`}
            passHref
            legacyBehavior
          >
            <StyledLink
              small
              target="_blank"
              isActive={false}
              className="z-10 mr-px-20 md:mr-px-40"
            >
              Investor Relations
            </StyledLink>
          </Link>
        ) : (
          <Link
            href={`https://accentro.de/${
              IS_ACCENTRO_INTERNATIONAL ? 'en' : ''
            }`}
            passHref
            legacyBehavior
          >
            <StyledLink
              small
              target="_blank"
              isActive={false}
              className="z-10 mr-px-20 md:mr-px-40"
            >
              ACCENTRO GmbH
            </StyledLink>
          </Link>
        )}
        <LocaleSwitcher />
      </div>
      {/* {SHOW_NAV_LOGIN && <TopNavLogin />} */}
    </>
  )
}
const NavBarContainer = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<{
    forceDark?: boolean
    mobileNavOpen?: boolean
    className?: string
  }> &
    React.ComponentProps<typeof motion.div>
>(
  (
    { children, forceDark, mobileNavOpen: _mobileNavOpen, className, ...rest },
    ref
  ) => (
    <motion.div
      ref={ref}
      className={cn(
        'fixed top-0 w-full z-30',
        !forceDark && 'text-white',
        className
      )}
      {...rest}
    >
      {children}
    </motion.div>
  )
)

NavBarContainer.displayName = 'NavBarContainer'

type StyledLinkProps = {
  isActive?: boolean
  small?: boolean
  className?: string
} & React.ComponentProps<'a'>

export const StyledLink = forwardRef<HTMLAnchorElement, StyledLinkProps>(
  ({ isActive, small, className, ...rest }, ref) => (
    <a
      ref={ref}
      className={cn(
        'font-semibold text-px-16 border-b-2 border-b-transparent inline-block hover:border-current group-hover:border-accentroWhite-full',
        'lg:text-px-20',
        small && 'ml:text-px-16',
        isActive && 'border-b-current',
        className
      )}
      {...rest}
    />
  )
)

StyledLink.displayName = 'StyledLink'

type StyledNavTextProps = {
  isActive?: boolean
  className?: string
} & React.ComponentProps<'span'>
export const StyledNavText = forwardRef<HTMLSpanElement, StyledNavTextProps>(
  ({ isActive, className, ...rest }, ref) => (
    <span
      ref={ref}
      className={cn(
        'font-semibold border-b-2 border-b-transparent text-px-16 inline-block lg:text-px-20 hover:border-current group-hover:border-accentroWhite-full',
        isActive && 'border-b-current',
        className
      )}
      {...rest}
    />
  )
)

StyledNavText.displayName = 'StyledNavText'

type StyledSubNavTextProps = {
  isActive?: boolean
  className?: string
} & React.ComponentProps<'span'>
export const StyledSubNavText = forwardRef<
  HTMLSpanElement,
  StyledSubNavTextProps
>(({ isActive, className, ...rest }, ref) => (
  <span
    ref={ref}
    className={clsxMerge(
      'text-accentroWhite-full inline-block ml:font-semibold text-px-16 ml:text-px-16 lg:text-px-16',
      isActive && 'border-b-accentroWhite-full border-b-2',
      className
    )}
    {...rest}
  />
))

StyledSubNavText.displayName = 'StyledSubNavText'

type StyledButtonProps = {
  isActive?: boolean
  className?: string
} & React.ComponentProps<'button'>
export const StyledButton = ({
  isActive,
  className,
  ...rest
}: StyledButtonProps) => (
  <button
    className={cn(
      'font-semibold text-px-16 border-b-2 border-b-transparent inline-block lg:text-px-20 hover:border-current group-hover:border-accentroWhite-full',
      isActive && 'border-b-current',
      className
    )}
    {...rest}
  />
)

const NavbarElementWrapper = ({
  showContactHeader,
  children,
}: React.PropsWithChildren<{ showContactHeader?: boolean }>) => (
  <div className={cn('hidden md:block', showContactHeader && 'md:mt-px-24')}>
    {children}
  </div>
)

const NavbarElement = ({
  children,
  className,
  // keeping props for parity though unused
  isAtTop,
  isScrollingDown,
  ...rest
}: React.PropsWithChildren<{
  isAtTop?: boolean
  isScrollingDown?: boolean
  className?: string
}> &
  React.ComponentProps<typeof motion.div>) => (
  <motion.div
    className={cn(
      'h-px-128 px-px-24 py-px-32 relative sm:h-[132px] md:h-[150px] md:px-px-48 ml:flex ml:flex-row ml:justify-between ml:items-center lg:px-px-64 lg:h-px-196',
      className
    )}
    {...rest}
  >
    {children}
  </motion.div>
)

export const NavbarSpaceBlocker = ({ isHidden }: { isHidden?: boolean }) => (
  <div
    className={cn(
      'bg-transparent min-h-px-128 sm:min-h-[132px] md:min-h-[150px] lg:min-h-px-196',
      isHidden && 'hidden'
    )}
  />
)

const StyledNavbarRightElements = ({
  absolute,
  children,
}: React.PropsWithChildren<{ absolute?: boolean }>) => (
  <div
    className={cn(
      (IS_ACCENTRO_WEB || (!IS_ACCENTRO_WEB && absolute)) &&
        'absolute sm:top-px-32 sm:right-px-24 md:top-px-32 md:right-px-48 ml:top-px-24 ml:right-px-48 lg:top-px-32 lg:right-px-64'
    )}
  >
    {children}
  </div>
)

const EntryWithSubnavbarWrapper = ({ children }: React.PropsWithChildren) => (
  <div className="flex flex-row flex-1 z-10 items-center mt-px-32 justify-center space-x-px-16 sm:justify-start md:space-x-px-24 ml:mt-0 ml:justify-center">
    {children}
  </div>
)

const NavItemWrapper = ({ children }: React.PropsWithChildren) => (
  <div className="bg-accentroGray-full text-accentroWhite-full px-px-32 w-full">
    {children}
  </div>
)

const NavbarContainerVariants = {
  isAtTop: {
    backgroundColor: '#ffffff00',
    translateY: '0%',
    transition: {
      duration: 0.1,
    },
  },
  scrollingUpOverHero: {
    backgroundColor: '#ffffff00',
    translateY: '0%',
  },
  scrollingDown: {
    translateY: '-100%',
    transition: {
      ease: 'linear' as const,
    },
  },
  scrollingUp: {
    translateY: '0%',
    backgroundColor: '#ffffff',
    transition: {
      ease: 'linear' as const,
    },
  },
  mobileNavOpen: {
    backgroundColor: '#2B2D36',
  },
  mobileNavClosed: {
    backgroundColor: '#ffffff00',
  },
}

export const NavBar = ({
  forceDarkTextColor,
  standalone,
  isProjectAndProperty,
  showContactHeader,
  hasHero,
  heroSize,
}: React.PropsWithChildren<NavbarPropsWithHero>) => {
  const { isScrollingDown } = useScrollDirection()
  const entries = useNavBarEntries('TopNav')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const { pageYOffset, isAtTop } = usePageYOffset()
  const heroHeights = useResponsiveHeroVariants()
  const navbarElementVariants = useResponsiveNavbarVariants()
  const height = useMemo(() => {
    if (heroSize) {
      return heroHeights[heroSize] - navbarElementVariants.isAtTop.height
    }
  }, [heroHeights, heroSize, navbarElementVariants])

  const hightExist = !!height
  const navigationOverHeroWhileScrollingUp = useMemo(() => {
    return (
      !isScrollingDown &&
      !isAtTop &&
      hasHero &&
      hightExist &&
      pageYOffset <= height
    )
  }, [isScrollingDown, isAtTop, hasHero, hightExist, pageYOffset, height])
  const navigationOutsideHeroWhileScrollingUp = useMemo(() => {
    return (
      !isScrollingDown &&
      !isAtTop &&
      hasHero &&
      hightExist &&
      pageYOffset > height
    )
  }, [isScrollingDown, isAtTop, hasHero, hightExist, pageYOffset, height])

  const topAndScrollingUp =
    !isScrollingDown && !isAtTop && !navigationOverHeroWhileScrollingUp
  const forceDark = forceDarkTextColor || topAndScrollingUp
  const userId = useUserId()
  const { loading, isLoggedIn } = useIsLoggedInWithLoading()
  const t = useTranslation()
  const { setOpen } = useAuthModalContext()

  const myAccentroNavEntry = useMyAccentroNavEntry()

  useRouteChange({
    event: 'routeChangeStart',
    callback: () => setMobileNavOpen(false),
  })

  const controls = useAnimation()

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isScrollingDown) setMobileNavOpen(false)
    if (isScrollingDown && !isAtTop) controls.start('scrollingDown')
    else if (
      (!isScrollingDown && !isAtTop && !hasHero) ||
      navigationOutsideHeroWhileScrollingUp
    )
      controls.start('scrollingUp')
    else if (!isScrollingDown && isAtTop) controls.start('isAtTop')
    else if (navigationOverHeroWhileScrollingUp) controls.start('isAtTop')
  }, [
    isAtTop,
    isScrollingDown,
    controls,
    hasHero,
    navigationOutsideHeroWhileScrollingUp,
    navigationOverHeroWhileScrollingUp,
  ])

  useEffect(() => {
    if (mobileNavOpen) controls.start('mobileNavOpen')
    else controls.start('mobileNavClosed')
  }, [mobileNavOpen, controls])

  const isMl = useIsMlOrGreater()

  return (
    <>
      {showContactHeader && <div className="h-px-32 bg-transparent" />}
      <NavbarSpaceBlocker isHidden={isProjectAndProperty} />

      <NavBarContainer
        ref={containerRef}
        mobileNavOpen={mobileNavOpen}
        forceDark={forceDark}
        animate={controls}
        transition={{
          type: 'tween' as const,
          ease: 'linear',
          duration: 0.2,
        }}
        variants={NavbarContainerVariants}
      >
        {showContactHeader && <ContactHeaderForNavBar displayOnTop />}

        <NavbarElementWrapper showContactHeader={showContactHeader}>
          <NavbarElement
            isAtTop={isAtTop}
            isScrollingDown={isScrollingDown}
            variants={navbarElementVariants}
          >
            <div>
              <NavBarLogo forceDarkTextColor={forceDark} />
            </div>
            <div className="flex flex-1">
              {!standalone && (
                <EntryWithSubnavbarWrapper>
                  {map(entries, (entry) => {
                    return (
                      <div
                        key={entry.id}
                        className="focus-visible:outline-none"
                      >
                        <div className="focus-visible:outline-none">
                          <EntryWithSubNavbar entry={entry} />
                        </div>
                      </div>
                    )
                  })}
                </EntryWithSubnavbarWrapper>
              )}
              {IS_ACCENTRO_WEB && (
                <div className="focus-visible:outline-none flex mt-px-32 sm:justify-end ml:mt-0 ml:justify-end ml:w-[230px]">
                  {loading ? (
                    <Skeleton animation="wave" className="w-px-128" />
                  ) : !isLoggedIn ? (
                    <>
                      <div className="pl-px-12 py-px-12 z-10">
                        <StyledButton
                          isActive={false}
                          className="z-10"
                          onClick={() => {
                            setOpen(true)
                          }}
                        >
                          {t('accentro:login')}
                        </StyledButton>
                      </div>
                    </>
                  ) : (
                    <div className="focus-visible:outline-none">
                      <EntryWithSubNavbar
                        shouldRoute={userId ? true : false}
                        alignRight
                        entry={myAccentroNavEntry}
                        postNode={
                          <>
                            <NavItemWrapper>
                              <div className="h-[2px] bg-accentroWhite-50 mt-px-16 mb-px-16" />
                            </NavItemWrapper>
                            <StyledNavEntry
                              onClick={() =>
                                signOut({
                                  callbackUrl: `${window.location.origin}`,
                                })
                              }
                              className="group"
                            >
                              <StyledSubNavText>
                                {t('accentro:logout')}
                              </StyledSubNavText>
                            </StyledNavEntry>
                          </>
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <StyledNavbarRightElements absolute={!isMl}>
              <NavbarRightElements />
            </StyledNavbarRightElements>
          </NavbarElement>
        </NavbarElementWrapper>
        <MobileNavBar
          standalone={standalone}
          controls={controls}
          forceDark={forceDark}
          entries={entries}
          mobileNavOpen={mobileNavOpen}
          setMobileNavOpen={setMobileNavOpen}
        />
      </NavBarContainer>
    </>
  )
}
